/**
 * Euchre — server-authoritative rules engine (4 players in two partnerships, 24-card deck).
 *
 * Rules implemented (toggles live in {@link EuchreConfig}):
 *  - 4 players in two partnerships: seats 0 & 2 (team 0) against seats 1 & 3 (team 1). The deck is 9 10 J Q K A of each
 *    suit; each player gets 5 cards, the next card is turned face up and three cards stay in the kitty.
 *  - Bidding round 1, left of the dealer: each player passes or "orders up" the turned card's suit as trump. If someone
 *    orders up, the dealer picks up the turned card and discards one card. If all four pass, the card is turned down and
 *    round 2 begins: each player in turn passes or names any suit except the turned-down suit. With `stickTheDealer`
 *    the dealer may not pass in round 2; otherwise a hand where everyone passes is thrown in and redealt.
 *  - The player who ordered up or named trump is the maker and may "go alone": their partner sits the hand out.
 *  - Trump ranks right bower (jack of trump), left bower (the other jack of the same colour, which belongs to the trump
 *    suit), A, K, Q, 10, 9. Other suits rank A K Q J 10 9. Players must follow the led suit (counting the left bower as
 *    a trump) when able. The player left of the dealer leads; the trick winner leads next.
 *  - Scoring per hand: if the makers win 3 or 4 tricks they score 1; all 5 tricks ("march") scores 2, or 4 when going
 *    alone. If the makers win fewer than 3 they are "euchred" and the defenders score 2.
 *  - The match ends when a team reaches `targetScore` or after `maxRounds`. Partners share a place.
 */
import { parseCard, removeCards, sortHand, SUITS, type CardId, type Suit } from '../../core/cards.js';
import { checkBool, checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface EuchreConfig {
  targetScore: number;
  /** Hard cap on hands so a match always terminates. */
  maxRounds: number;
  /** The dealer must name trump in round 2 when everyone else passed (otherwise the hand is thrown in). */
  stickTheDealer: boolean;
  allowAlone: boolean;
  roundEndDelayMs: number;
  trickEndDelayMs: number;
}

export type EuchrePhase = 'BID1' | 'DISCARD' | 'BID2' | 'PLAYING' | 'TRICK_END' | 'ROUND_END' | 'MATCH_END';

export interface EuchreTrickPlay {
  seat: number;
  card: CardId;
}

export interface EuchreCompletedTrick {
  leader: number;
  plays: EuchreTrickPlay[];
  winner: number;
}

/** Per-round result for one team (public once the round ends). */
export interface EuchreTeamRound {
  maker: boolean;
  tricks: number;
  alone: boolean;
  euchred: boolean;
  trump: Suit;
  /** Points scored this round. */
  delta: number;
}

/** What a seat did during the auction: nothing yet, passed, ordered up the turned card, or named a suit. */
export type EuchreBidMark = null | 'pass' | 'order' | Suit;

export interface EuchreState {
  config: EuchreConfig;
  players: string[];
  round: number;
  dealer: number;
  phase: EuchrePhase;
  hands: CardId[][];
  kitty: CardId[];
  turnCard: CardId;
  bids: EuchreBidMark[];
  turn: number;
  trump: Suit | null;
  maker: number | null;
  alone: boolean;
  sittingOut: number | null;
  tricksWon: number[];
  trick: EuchreTrickPlay[];
  tricks: EuchreCompletedTrick[];
  teamScores: number[];
  roundResults: EuchreTeamRound[][];
  /** Hands thrown in a row (only when `stickTheDealer` is off). */
  redeals: number;
}

export type EuchreAction =
  | { type: 'pass' }
  | { type: 'orderUp'; alone: boolean }
  | { type: 'call'; suit: Suit; alone: boolean }
  | { type: 'discard'; card: CardId }
  | { type: 'play'; card: CardId };

export type EuchreLegal =
  | { kind: 'bid'; round: 1 | 2; suits: Suit[]; canPass: boolean; canAlone: boolean }
  | { kind: 'discard'; cards: CardId[] }
  | { kind: 'play'; cards: CardId[] }
  | { kind: 'none' };

export interface EuchreView {
  gameType: 'EUCHRE';
  config: EuchreConfig;
  seat: number | null;
  phase: EuchrePhase;
  round: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  /** The turned-up card; only shown while it is still in play (round 1 and the dealer's pick-up). */
  turnCard: CardId | null;
  /** Suit of the turned card — in round 2 it is the suit that may not be named. */
  turnSuit: Suit;
  bids: EuchreBidMark[];
  trump: Suit | null;
  maker: number | null;
  alone: boolean;
  sittingOut: number | null;
  tricksWon: number[];
  trick: EuchreTrickPlay[];
  lastTrick: EuchreCompletedTrick | null;
  tricks: EuchreCompletedTrick[];
  teamScores: number[];
  roundResults: EuchreTeamRound[][];
  /** Score of each seat's team (so generic score UIs work). */
  totals: number[];
}

const DEFAULTS: EuchreConfig = {
  targetScore: 10,
  maxRounds: 20,
  stickTheDealer: true,
  allowAlone: true,
  roundEndDelayMs: 5000,
  trickEndDelayMs: 900,
};

const SEATS = 4;
const TRICKS = 5;
const MAX_REDEALS = 3;
const RANKS = [9, 10, 11, 12, 13, 14] as const;
const RANK_CHAR: Record<number, string> = { 9: '9', 10: 'T', 11: 'J', 12: 'Q', 13: 'K', 14: 'A' };
const SAME_COLOUR: Record<Suit, Suit> = { S: 'C', C: 'S', H: 'D', D: 'H' };

export const euchreTeamOf = (seat: number): number => seat % 2;
export const euchrePartnerOf = (seat: number): number => (seat + 2) % SEATS;

export function euchreDeck(): CardId[] {
  return SUITS.flatMap((s) => RANKS.map((r) => `${RANK_CHAR[r]}${s}`));
}

/** The suit a card counts as: the left bower belongs to the trump suit. */
export function euchreSuit(card: CardId, trump: Suit): Suit {
  const c = parseCard(card);
  return c.rank === 11 && c.suit === SAME_COLOUR[trump] ? trump : c.suit;
}

/** Strength among trumps (right bower 30, left bower 29, then A..9), or null for a non-trump card. */
export function euchreTrumpPower(card: CardId, trump: Suit): number | null {
  const c = parseCard(card);
  if (c.rank === 11 && c.suit === trump) return 30;
  if (c.rank === 11 && c.suit === SAME_COLOUR[trump]) return 29;
  return c.suit === trump ? c.rank : null;
}

export function euchreTrickWinner(plays: readonly EuchreTrickPlay[], trump: Suit): number {
  const led = euchreSuit(plays[0]!.card, trump);
  const value = (card: CardId): number => {
    const p = euchreTrumpPower(card, trump);
    if (p !== null) return 100 + p;
    return euchreSuit(card, trump) === led ? parseCard(card).rank : 0;
  };
  let best = plays[0]!;
  for (const p of plays.slice(1)) if (value(p.card) > value(best.card)) best = p;
  return best.seat;
}

export function euchreLegalCards(hand: readonly CardId[], trick: readonly EuchreTrickPlay[], trump: Suit): CardId[] {
  if (trick.length === 0) return [...hand];
  const led = euchreSuit(trick[0]!.card, trump);
  const followers = hand.filter((c) => euchreSuit(c, trump) === led);
  return followers.length > 0 ? followers : [...hand];
}

/** Hand points for the makers/defenders. */
export function euchreScoreRound(makerTricks: number, alone: boolean): { maker: number; defender: number } {
  if (makerTricks >= 3) return { maker: makerTricks === TRICKS ? (alone ? 4 : 2) : 1, defender: 0 };
  return { maker: 0, defender: 2 };
}

/** Most valuable suit to name: by trump length, bowers and honours. */
export function euchreBestSuit(hand: readonly CardId[], exclude: Suit | null = null): Suit {
  let best: Suit | null = null;
  let bestScore = -1;
  for (const suit of SUITS) {
    if (suit === exclude) continue;
    const score = hand.reduce((n, c) => {
      const p = euchreTrumpPower(c, suit);
      return p === null ? n : n + 1 + (p >= 29 ? 2 : p >= 13 ? 1 : 0);
    }, 0);
    if (score > bestScore) {
      bestScore = score;
      best = suit;
    }
  }
  return best!;
}

/** The card to throw away: the lowest non-trump, keeping trumps and aces where possible. */
export function euchreDiscardChoice(hand: readonly CardId[], trump: Suit): CardId {
  const worth = (c: CardId) => {
    const p = euchreTrumpPower(c, trump);
    return p !== null ? 100 + p : parseCard(c).rank;
  };
  return [...hand].sort((a, b) => worth(a) - worth(b))[0]!;
}

function activeSeats(state: Pick<EuchreState, 'sittingOut'>): number {
  return state.sittingOut === null ? SEATS : SEATS - 1;
}

function nextActive(state: Pick<EuchreState, 'sittingOut'>, from: number): number {
  let s = (from + 1) % SEATS;
  if (s === state.sittingOut) s = (s + 1) % SEATS;
  return s;
}

function deal(state: EuchreState, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(euchreDeck(), rng);
  const hands: CardId[][] = [[], [], [], []];
  for (let i = 0; i < SEATS * TRICKS; i++) hands[i % SEATS]!.push(deck[i]!);
  state.hands = hands.map((h) => sortHand(h));
  state.turnCard = deck[SEATS * TRICKS]!;
  state.kitty = deck.slice(SEATS * TRICKS + 1);
  state.bids = [null, null, null, null];
  state.trump = null;
  state.maker = null;
  state.alone = false;
  state.sittingOut = null;
  state.tricksWon = [0, 0, 0, 0];
  state.trick = [];
  state.tricks = [];
  state.phase = 'BID1';
  state.turn = (state.dealer + 1) % SEATS;
  events.push({ type: 'dealt', round: state.round, dealer: state.dealer, turnCard: state.turnCard });
}

/** Trump is set: sort out who sits out and start the play (or the dealer's pick-up). */
function startPlay(state: EuchreState, events: EngineEvent[]): void {
  state.phase = 'PLAYING';
  state.trick = [];
  state.tricks = [];
  state.turn = nextActive(state, state.dealer);
  events.push({ type: 'playStart', leader: state.turn, trump: state.trump, maker: state.maker, alone: state.alone });
}

function makeMaker(state: EuchreState, seat: number, suit: Suit, alone: boolean, events: EngineEvent[]): void {
  state.trump = suit;
  state.maker = seat;
  state.alone = alone;
  state.sittingOut = alone ? euchrePartnerOf(seat) : null;
  events.push({ type: 'trumpChosen', seat, suit, alone });
}

const turnSuit = (state: Pick<EuchreState, 'turnCard'>): Suit => parseCard(state.turnCard).suit;

/** In round 2 the dealer must name trump when nobody else did (stick the dealer, or after too many redeals). */
function dealerForced(state: EuchreState, seat: number): boolean {
  return state.phase === 'BID2' && seat === state.dealer && (state.config.stickTheDealer || state.redeals >= MAX_REDEALS);
}

export const euchreEngine: GameEngine<EuchreConfig, EuchreState, EuchreAction, EuchreView, EuchreLegal> = {
  gameType: 'EUCHRE',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 3, 30);
    checkInt('maxRounds', c.maxRounds, 1, 60);
    checkBool('stickTheDealer', c.stickTheDealer);
    checkBool('allowAlone', c.allowAlone);
    checkInt('roundEndDelayMs', c.roundEndDelayMs, 0, 60000);
    checkInt('trickEndDelayMs', c.trickEndDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Euchre needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: EuchreState = {
      config,
      players,
      round: 1,
      dealer: rng.int(SEATS),
      phase: 'BID1',
      hands: [],
      kitty: [],
      turnCard: '',
      bids: [],
      turn: 0,
      trump: null,
      maker: null,
      alone: false,
      sittingOut: null,
      tricksWon: [],
      trick: [],
      tricks: [],
      teamScores: [0, 0],
      roundResults: [],
      redeals: 0,
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'pass') return { type: 'pass' };
    if (raw.type === 'orderUp') return { type: 'orderUp', alone: raw.alone === true };
    if (raw.type === 'call' && typeof raw.suit === 'string' && (SUITS as readonly string[]).includes(raw.suit)) {
      return { type: 'call', suit: raw.suit as Suit, alone: raw.alone === true };
    }
    if (raw.type === 'discard' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'discard', card: raw.card };
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'play', card: raw.card };
    throw new EngineError('INVALID_ACTION', 'Unknown Euchre action');
  },

  applyAction(state, seat, action, rng) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    const acting = state.phase === 'BID1' || state.phase === 'BID2' || state.phase === 'DISCARD' || state.phase === 'PLAYING';
    assertEngine(acting && seat === state.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];

    if (action.type === 'pass' || action.type === 'orderUp' || action.type === 'call') {
      assertEngine(state.phase === 'BID1' || state.phase === 'BID2', 'INVALID_PHASE', 'Bidding is closed');
      if (action.type === 'pass') {
        assertEngine(!dealerForced(state, seat), 'ILLEGAL_MOVE', 'The dealer must name trump');
        state.bids[seat] = 'pass';
        events.push({ type: 'pass', seat });
        if (seat === state.dealer) {
          if (state.phase === 'BID1') {
            state.phase = 'BID2';
            state.bids = [null, null, null, null];
            state.turn = (state.dealer + 1) % SEATS;
            events.push({ type: 'turnedDown', card: state.turnCard });
          } else {
            // Everyone passed twice: throw the hand in.
            state.redeals++;
            state.dealer = (state.dealer + 1) % SEATS;
            events.push({ type: 'thrownIn' });
            deal(state, rng, events);
          }
        } else {
          state.turn = (seat + 1) % SEATS;
        }
        return events;
      }
      if (action.alone) assertEngine(state.config.allowAlone, 'ILLEGAL_MOVE', 'Going alone is not allowed at this table');
      if (action.type === 'orderUp') {
        assertEngine(state.phase === 'BID1', 'INVALID_PHASE', 'The turned card is already down');
        state.bids[seat] = 'order';
        makeMaker(state, seat, turnSuit(state), action.alone, events);
        // The dealer takes the turned card and discards one — unless the dealer is the partner sitting out.
        if (state.sittingOut === state.dealer) {
          startPlay(state, events);
        } else {
          state.hands[state.dealer]!.push(state.turnCard);
          state.hands[state.dealer] = sortHand(state.hands[state.dealer]!);
          state.phase = 'DISCARD';
          state.turn = state.dealer;
          events.push({ type: 'pickedUp', seat: state.dealer, card: state.turnCard });
        }
        return events;
      }
      assertEngine(state.phase === 'BID2', 'INVALID_PHASE', 'Name trump in the second round only');
      assertEngine(action.suit !== turnSuit(state), 'ILLEGAL_MOVE', 'You cannot name the suit that was turned down');
      state.bids[seat] = action.suit;
      makeMaker(state, seat, action.suit, action.alone, events);
      startPlay(state, events);
      return events;
    }

    if (action.type === 'discard') {
      assertEngine(state.phase === 'DISCARD', 'INVALID_PHASE', 'Nothing to discard now');
      const hand = state.hands[seat]!;
      assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
      removeCards(hand, [action.card]);
      events.push({ type: 'discarded', seat });
      startPlay(state, events);
      return events;
    }

    assertEngine(state.phase === 'PLAYING', 'INVALID_PHASE', 'Not in play phase');
    const hand = state.hands[seat]!;
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    assertEngine(euchreLegalCards(hand, state.trick, state.trump!).includes(action.card), 'ILLEGAL_MOVE', 'You must follow suit');
    removeCards(hand, [action.card]);
    state.trick.push({ seat, card: action.card });
    events.push({ type: 'cardPlayed', seat, card: action.card });
    if (state.trick.length < activeSeats(state)) {
      state.turn = nextActive(state, seat);
      return events;
    }
    const winner = euchreTrickWinner(state.trick, state.trump!);
    state.tricksWon[winner]!++;
    state.tricks.push({ leader: state.trick[0]!.seat, plays: state.trick, winner });
    events.push({ type: 'trickWon', seat: winner, trickNo: state.tricks.length });
    state.phase = 'TRICK_END';
    state.turn = winner;
    return events;
  },

  getActors(state) {
    return state.phase === 'BID1' || state.phase === 'BID2' || state.phase === 'DISCARD' || state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (seat !== state.turn) return { kind: 'none' };
    const canAlone = state.config.allowAlone;
    if (state.phase === 'BID1') return { kind: 'bid', round: 1, suits: [turnSuit(state)], canPass: true, canAlone };
    if (state.phase === 'BID2') {
      return { kind: 'bid', round: 2, suits: SUITS.filter((s) => s !== turnSuit(state)), canPass: !dealerForced(state, seat), canAlone };
    }
    if (state.phase === 'DISCARD') return { kind: 'discard', cards: [...state.hands[seat]!] };
    if (state.phase === 'PLAYING') return { kind: 'play', cards: euchreLegalCards(state.hands[seat]!, state.trick, state.trump!) };
    return { kind: 'none' };
  },

  getTimeoutAction(state, seat) {
    if (state.phase === 'BID1') return { type: 'pass' };
    if (state.phase === 'BID2') {
      if (!dealerForced(state, seat)) return { type: 'pass' };
      return { type: 'call', suit: euchreBestSuit(state.hands[seat]!, turnSuit(state)), alone: false };
    }
    if (state.phase === 'DISCARD') return { type: 'discard', card: euchreDiscardChoice(state.hands[seat]!, state.trump!) };
    const trump = state.trump!;
    const legal = euchreLegalCards(state.hands[seat]!, state.trick, trump);
    // Lowest legal card, keeping trumps for last: rarely wastes a winner, never illegal.
    const worth = (c: CardId) => {
      const p = euchreTrumpPower(c, trump);
      return p !== null ? 100 + p : parseCard(c).rank;
    };
    return { type: 'play', card: [...legal].sort((a, b) => worth(a) - worth(b))[0]! };
  },

  getView(state, seat) {
    const turnUp = state.phase === 'BID1' || state.phase === 'DISCARD';
    return {
      gameType: 'EUCHRE',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.round,
      dealer: state.dealer,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      turnCard: turnUp ? state.turnCard : null,
      turnSuit: turnSuit(state),
      bids: [...state.bids],
      trump: state.trump,
      maker: state.maker,
      alone: state.alone,
      sittingOut: state.sittingOut,
      tricksWon: [...state.tricksWon],
      trick: state.phase === 'TRICK_END' ? [...(state.tricks.at(-1)?.plays ?? [])] : [...state.trick],
      lastTrick: state.tricks.at(-1) ?? null,
      tricks: state.tricks.map((t) => ({ ...t, plays: [...t.plays] })),
      teamScores: [...state.teamScores],
      roundResults: state.roundResults.map((r) => r.map((x) => ({ ...x }))),
      totals: [0, 1, 2, 3].map((s) => state.teamScores[euchreTeamOf(s)]!),
    };
  },

  getSystemStep(state) {
    if (state.phase === 'TRICK_END') return { kind: 'collectTrick', delayMs: state.config.trickEndDelayMs };
    if (state.phase === 'ROUND_END') return { kind: 'nextRound', delayMs: state.config.roundEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'TRICK_END') {
      state.trick = [];
      if (state.tricks.length < TRICKS) {
        state.phase = 'PLAYING';
        events.push({ type: 'trickCollected', leader: state.turn });
        return events;
      }
      const cfg = state.config;
      const makerTeam = euchreTeamOf(state.maker!);
      const teamTricks = (team: number) => state.tricksWon[team]! + state.tricksWon[team + 2]!;
      const pts = euchreScoreRound(teamTricks(makerTeam), state.alone);
      const results: EuchreTeamRound[] = [0, 1].map((team) => ({
        maker: team === makerTeam,
        tricks: teamTricks(team),
        alone: team === makerTeam && state.alone,
        euchred: team === makerTeam && teamTricks(team) < 3,
        trump: state.trump!,
        delta: team === makerTeam ? pts.maker : pts.defender,
      }));
      results.forEach((r, team) => {
        state.teamScores[team]! += r.delta;
      });
      state.roundResults.push(results);
      events.push({ type: 'roundEnd', round: state.round, results, teamScores: [...state.teamScores], tricksWon: [...state.tricksWon] });
      const reached = state.teamScores.some((s) => s >= cfg.targetScore);
      if (reached || state.round >= cfg.maxRounds) {
        state.phase = 'MATCH_END';
        events.push({ type: 'matchEnd', standings: teamStandings(state) });
      } else {
        state.phase = 'ROUND_END';
      }
      return events;
    }
    if (state.phase === 'ROUND_END') {
      state.round++;
      state.dealer = (state.dealer + 1) % SEATS;
      state.redeals = 0;
      deal(state, rng, events);
      return events;
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return teamStandings(state);
  },

  getRoundsCompleted: (state) => state.roundResults.length,
};

/** Partners share a place: the leading team is 1st, the other 2nd (both 1st on a tie). */
function teamStandings(state: EuchreState): Standing[] {
  const [a, b] = state.teamScores as [number, number];
  return [0, 1, 2, 3].map((seat) => {
    const team = euchreTeamOf(seat);
    const mine = team === 0 ? a : b;
    const theirs = team === 0 ? b : a;
    return { seat, score: mine, place: mine >= theirs ? 1 : 2 };
  });
}
